import { useState, useEffect } from 'react';
import axios from '@/utils/api';
import { toast } from 'sonner';
import { 
  Plus, Edit, Trash2, Power, PowerOff, Calendar, Users, 
  Sparkles, Crown, Gift, Zap, Star, Flame, Heart, Megaphone,
  Eye, EyeOff, ExternalLink, CreditCard, Upload, Video, Image as ImageIcon,
  Clock, Play, Settings, BarChart3, MousePointer, PlayCircle, TrendingUp, X,
  Smartphone, Monitor
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { format } from 'date-fns';
import { useDialog } from '@/context/DialogContext';

// Use same-origin API path on production custom domains
const API_BASE = window.location.hostname.includes('preview.emergentagent.com') || window.location.hostname === 'localhost'
  ? (process.env.REACT_APP_BACKEND_URL || '')
  : '';

const ICONS = [
  { value: 'sparkles', label: 'Sparkles', icon: Sparkles },
  { value: 'crown', label: 'Crown', icon: Crown },
  { value: 'gift', label: 'Gift', icon: Gift },
  { value: 'zap', label: 'Zap', icon: Zap },
  { value: 'star', label: 'Star', icon: Star },
  { value: 'fire', label: 'Fire', icon: Flame },
  { value: 'heart', label: 'Heart', icon: Heart },
  { value: 'megaphone', label: 'Megaphone', icon: Megaphone },
];

const TIERS = [
  { value: 'all', label: 'All Users' },
  { value: 'free', label: 'Free Users' },
  { value: 'founding_member', label: 'Founding Members' },
  { value: 'premium', label: 'Premium Users' },
];

const ACTIONS = [
  { value: 'link', label: 'External Link' },
  { value: 'upgrade', label: 'Open Upgrade Modal' },
  { value: 'dismiss', label: 'Dismiss Only' },
];

const BANNER_TYPES = [
  { value: 'styled', label: 'Styled Banner', icon: Sparkles, desc: 'Icon + gradient + text' },
  { value: 'image', label: 'Image Banner', icon: ImageIcon, desc: 'Upload custom image' },
  { value: 'video', label: 'Video Banner', icon: Video, desc: 'Upload or embed video' },
];

const PromoCampaignManager = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [selectedCampaignAnalytics, setSelectedCampaignAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [mobilePreview, setMobilePreview] = useState(false); // NEW: Mobile preview toggle
  const { confirm } = useDialog();
  const [globalSettings, setGlobalSettings] = useState({
    transition_time: 5, // seconds between slides
    auto_rotate: true,
    show_navigation: true,
    show_indicators: true,
  });
  const [formData, setFormData] = useState({
    banner_type: 'styled', // 'styled', 'image', or 'video'
    title: '',
    subtitle: '',
    description: '',
    cta_text: 'Learn More',
    cta_link: '',
    cta_action: 'link',
    stripe_price_id: '',
    discount_label: '',
    icon: 'sparkles',
    gradient_from: '#df1283',
    gradient_to: '#FF7901',
    // Media fields
    image_url: '',
    video_url: '',
    video_type: 'upload', // 'upload' or 'embed'
    // Targeting
    target_tiers: ['free', 'founding_member', 'premium'],
    is_active: false,
    start_date: '',
    end_date: '',
    priority: 0,
    // Per-campaign transition override (optional)
    custom_duration: null, // null = use global setting
    // Dismiss controls
    allow_dismiss: true, // Can users dismiss this promo?
    dismiss_allowed_tiers: ['premium', 'founding_member', 'enterprise'], // Which tiers can dismiss
    // Sponsored label
    is_sponsored: false, // Show "Sponsored" label
    sponsor_name: '', // Optional: "Sponsored by XYZ"
  });

  const fetchCampaigns = async () => {
    try {
      
      const res = await axios.get(`${API_BASE}/api/admin/promos`, {
        withCredentials: true
      });
      setCampaigns(res.data.campaigns || []);
    } catch (error) {
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const fetchGlobalSettings = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/admin/settings/promo-display`);
      if (res.data.settings) {
        setGlobalSettings(res.data.settings);
      }
    } catch (error) {
      // Use defaults - global settings are optional
      console.debug('Using default promo display settings:', error.message);
    }
  };

  const fetchAnalytics = async () => {
    setLoadingAnalytics(true);
    try {
      
      const res = await axios.get(`${API_BASE}/api/admin/promo-analytics`, {
        withCredentials: true
      });
      setAnalyticsData(res.data.campaigns || []);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const fetchCampaignAnalytics = async (campaignId) => {
    setLoadingAnalytics(true);
    try {
      
      const res = await axios.get(`${API_BASE}/api/admin/promo-analytics/${campaignId}`, {
        withCredentials: true
      });
      setSelectedCampaignAnalytics(res.data);
    } catch (error) {
      toast.error('Failed to load campaign analytics');
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const openAnalytics = () => {
    setShowAnalytics(true);
    fetchAnalytics();
  };

  useEffect(() => {
    fetchCampaigns();
    fetchGlobalSettings();
  }, []);

  const handleSaveSettings = async () => {
    try {
      await axios.put(`${API_BASE}/api/admin/settings/promo-display`, { settings: globalSettings });
      toast.success('Display settings saved!');
      setShowSettings(false);
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    
    try {
      if (editingCampaign) {
        await axios.put(
          `${API_BASE}/api/admin/promos/${editingCampaign.id}`,
          formData,
          { withCredentials: true }
        );
        toast.success('Campaign updated!');
      } else {
        await axios.post(
          `${API_BASE}/api/admin/promos`,
          formData,
          { withCredentials: true }
        );
        toast.success('Campaign created!');
      }
      
      setShowEditor(false);
      setEditingCampaign(null);
      resetForm();
      fetchCampaigns();
    } catch (error) {
      toast.error('Failed to save campaign');
    }
  };

  const handleEdit = (campaign) => {
    setEditingCampaign(campaign);
    setFormData({
      banner_type: campaign.banner_type || 'styled',
      title: campaign.title || '',
      subtitle: campaign.subtitle || '',
      description: campaign.description || '',
      cta_text: campaign.cta_text || 'Learn More',
      cta_link: campaign.cta_link || '',
      cta_action: campaign.cta_action || 'link',
      stripe_price_id: campaign.stripe_price_id || '',
      discount_label: campaign.discount_label || '',
      icon: campaign.icon || 'sparkles',
      gradient_from: campaign.gradient_from || '#df1283',
      gradient_to: campaign.gradient_to || '#FF7901',
      image_url: campaign.image_url || '',
      video_url: campaign.video_url || '',
      video_type: campaign.video_type || 'upload',
      target_tiers: campaign.target_tiers || ['free', 'founding_member', 'premium'],
      is_active: campaign.is_active || false,
      start_date: campaign.start_date || '',
      end_date: campaign.end_date || '',
      priority: campaign.priority || 0,
      custom_duration: campaign.custom_duration || null,
    });
    setShowEditor(true);
  };

  const handleDelete = async (campaignId) => {
    const confirmed = await confirm({
      title: 'Delete Campaign',
      message: 'Are you sure you want to delete this campaign? This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'destructive'
    });
    if (!confirmed) return;
    
    
    try {
      await axios.delete(`${API_BASE}/api/admin/promos/${campaignId}`, {
        withCredentials: true
      });
      toast.success('Campaign deleted');
      fetchCampaigns();
    } catch (error) {
      toast.error('Failed to delete campaign');
    }
  };

  const handleToggle = async (campaignId) => {
    
    try {
      const res = await axios.post(
        `${API_BASE}/api/admin/promos/${campaignId}/toggle`,
        {},
        { withCredentials: true }
      );
      toast.success(res.data.is_active ? 'Campaign activated!' : 'Campaign deactivated');
      fetchCampaigns();
    } catch (error) {
      toast.error('Failed to toggle campaign');
    }
  };

  const resetForm = () => {
    setFormData({
      banner_type: 'styled',
      title: '',
      subtitle: '',
      description: '',
      cta_text: 'Learn More',
      cta_link: '',
      cta_action: 'link',
      stripe_price_id: '',
      discount_label: '',
      icon: 'sparkles',
      gradient_from: '#df1283',
      gradient_to: '#FF7901',
      image_url: '',
      video_url: '',
      video_type: 'upload',
      target_tiers: ['free', 'founding_member', 'premium'],
      is_active: false,
      start_date: '',
      end_date: '',
      priority: 0,
      custom_duration: null,
    });
  };

  const handleMediaUpload = async (e, mediaType) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isVideo = mediaType === 'video';
    const validTypes = isVideo 
      ? ['video/mp4', 'video/webm', 'video/ogg']
      : ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const maxSize = isVideo ? 50 * 1024 * 1024 : 2 * 1024 * 1024; // 50MB for video, 2MB for image

    if (!validTypes.includes(file.type)) {
      toast.error(`Please upload a valid ${mediaType} file`);
      return;
    }

    if (file.size > maxSize) {
      toast.error(`${mediaType === 'video' ? 'Video' : 'Image'} must be under ${isVideo ? '50MB' : '2MB'}`);
      return;
    }

    setUploadingMedia(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    // Get admin token from localStorage
    const adminToken = localStorage.getItem('adminToken');

    try {
      
      const res = await axios.post(`${API_BASE}/api/upload-media`, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-Admin-Token': adminToken
        }
      });
      
      if (res.data.url) {
        if (isVideo) {
          setFormData(prev => ({ ...prev, video_url: res.data.url }));
        } else {
          setFormData(prev => ({ ...prev, image_url: res.data.url }));
        }
        toast.success(`${mediaType === 'video' ? 'Video' : 'Image'} uploaded!`);
      }
    } catch (error) {
      toast.error(`Failed to upload ${mediaType}`);
    } finally {
      setUploadingMedia(false);
    }
  };

  const toggleTier = (tier) => {
    setFormData(prev => ({
      ...prev,
      target_tiers: prev.target_tiers.includes(tier)
        ? prev.target_tiers.filter(t => t !== tier)
        : [...prev.target_tiers, tier]
    }));
  };

  const IconPreview = ICONS.find(i => i.value === formData.icon)?.icon || Sparkles;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-[#FF7901]" />
            Promotional Campaigns
          </h2>
          <p className="text-sm text-[#FDF2F8]/60 mt-1">
            Create rotating banners for upgrades, events, sales, and promotions
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={openAnalytics}
            variant="outline"
            className="border-[#0FFFE2]/50 text-[#0FFFE2] hover:text-white hover:border-[#0FFFE2] hover:bg-[#0FFFE2]/10"
            data-testid="promo-analytics-btn"
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            Analytics
          </Button>
          <Button
            onClick={() => setShowSettings(true)}
            variant="outline"
            className="border-[#4A0A66] text-[#FDF2F8]/70 hover:text-white hover:border-[#df1283]"
          >
            <Settings className="w-4 h-4 mr-2" />
            Display Settings
          </Button>
          <Button
            onClick={() => {
              resetForm();
              setEditingCampaign(null);
              setShowEditor(true);
            }}
            className="bg-gradient-to-r from-[#df1283] to-[#FF7901] text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Campaign
          </Button>
        </div>
      </div>

      {/* Display Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border-2 border-[#df1283]/50 rounded-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Settings className="w-5 h-5 text-[#FF7901]" />
              Banner Display Settings
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-[#FDF2F8]/70 mb-2">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Transition Time (seconds)
                </label>
                <Input
                  type="number"
                  min="2"
                  max="30"
                  value={globalSettings.transition_time}
                  onChange={(e) => setGlobalSettings(prev => ({ ...prev, transition_time: parseInt(e.target.value) || 5 }))}
                  className="bg-[#1a0527] border-[#4A0A66] text-white"
                />
                <p className="text-xs text-[#FDF2F8]/50 mt-1">How long each banner stays before rotating</p>
              </div>

              <div className="flex items-center justify-between">
                <label className="text-sm text-[#FDF2F8]/70">Auto-rotate banners</label>
                <button
                  type="button"
                  onClick={() => setGlobalSettings(prev => ({ ...prev, auto_rotate: !prev.auto_rotate }))}
                  className={`w-12 h-6 rounded-full transition-colors relative ${
                    globalSettings.auto_rotate ? 'bg-green-500' : 'bg-gray-600'
                  }`}
                >
                  <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                    globalSettings.auto_rotate ? 'left-7' : 'left-1'
                  }`} />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <label className="text-sm text-[#FDF2F8]/70">Show navigation arrows</label>
                <button
                  type="button"
                  onClick={() => setGlobalSettings(prev => ({ ...prev, show_navigation: !prev.show_navigation }))}
                  className={`w-12 h-6 rounded-full transition-colors relative ${
                    globalSettings.show_navigation ? 'bg-green-500' : 'bg-gray-600'
                  }`}
                >
                  <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                    globalSettings.show_navigation ? 'left-7' : 'left-1'
                  }`} />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <label className="text-sm text-[#FDF2F8]/70">Show dot indicators</label>
                <button
                  type="button"
                  onClick={() => setGlobalSettings(prev => ({ ...prev, show_indicators: !prev.show_indicators }))}
                  className={`w-12 h-6 rounded-full transition-colors relative ${
                    globalSettings.show_indicators ? 'bg-green-500' : 'bg-gray-600'
                  }`}
                >
                  <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                    globalSettings.show_indicators ? 'left-7' : 'left-1'
                  }`} />
                </button>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-[#4A0A66]">
              <Button
                type="button"
                onClick={() => setShowSettings(false)}
                variant="ghost"
                className="text-[#FDF2F8]/60"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveSettings}
                className="bg-gradient-to-r from-[#df1283] to-[#FF7901] text-white"
              >
                Save Settings
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Campaign Editor Modal */}
      {showEditor && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border-2 border-[#df1283]/50 rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-white mb-4">
              {editingCampaign ? 'Edit Campaign' : 'Create Campaign'}
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Banner Type Toggle */}
              <div className="mb-4">
                <span className="text-sm text-[#FDF2F8]/60 block mb-2">Banner Type:</span>
                <div className="grid grid-cols-3 gap-2">
                  {BANNER_TYPES.map(type => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, banner_type: type.value }))}
                      className={`p-3 rounded-lg border transition-all text-center ${
                        formData.banner_type === type.value
                          ? 'border-[#df1283] bg-[#df1283]/20'
                          : 'border-[#4A0A66] hover:border-[#df1283]/50'
                      }`}
                    >
                      <type.icon className={`w-5 h-5 mx-auto mb-1 ${
                        formData.banner_type === type.value ? 'text-[#df1283]' : 'text-[#FDF2F8]/60'
                      }`} />
                      <p className={`text-sm font-medium ${
                        formData.banner_type === type.value ? 'text-white' : 'text-[#FDF2F8]/70'
                      }`}>{type.label}</p>
                      <p className="text-xs text-[#FDF2F8]/40">{type.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Preview Toggle - Desktop/Mobile */}
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-[#FDF2F8]/60">Preview:</span>
                <div className="flex items-center gap-1 bg-[#1a0527] rounded-lg p-1">
                  <button
                    type="button"
                    onClick={() => setMobilePreview(false)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs transition-colors ${
                      !mobilePreview 
                        ? 'bg-[#df1283] text-white' 
                        : 'text-[#FDF2F8]/60 hover:text-white'
                    }`}
                  >
                    <Monitor className="w-3.5 h-3.5" />
                    Desktop
                  </button>
                  <button
                    type="button"
                    onClick={() => setMobilePreview(true)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs transition-colors ${
                      mobilePreview 
                        ? 'bg-[#df1283] text-white' 
                        : 'text-[#FDF2F8]/60 hover:text-white'
                    }`}
                  >
                    <Smartphone className="w-3.5 h-3.5" />
                    Mobile
                  </button>
                </div>
              </div>

              {/* Preview Container - Responsive wrapper */}
              <div className={`mx-auto transition-all duration-300 ${mobilePreview ? 'max-w-[320px]' : 'w-full'}`}>
                {/* Preview */}
                {formData.banner_type === 'styled' ? (
                  <div 
                    className="rounded-xl p-4 mb-4"
                    style={{
                      background: `linear-gradient(135deg, ${formData.gradient_from}30 0%, ${formData.gradient_to}30 100%)`,
                      border: `1px solid ${formData.gradient_from}50`,
                    }}
                  >
                    <div className={`flex ${mobilePreview ? 'flex-col gap-3' : 'items-center gap-3'}`}>
                      <div className="flex items-center gap-3">
                        <div 
                          className={`rounded-xl flex items-center justify-center flex-shrink-0 ${mobilePreview ? 'w-10 h-10' : 'w-12 h-12'}`}
                          style={{
                            background: `linear-gradient(135deg, ${formData.gradient_from} 0%, ${formData.gradient_to} 100%)`,
                          }}
                        >
                          <IconPreview className={`text-white ${mobilePreview ? 'w-5 h-5' : 'w-6 h-6'}`} />
                        </div>
                        <div className="min-w-0">
                          <p className={`font-bold text-white ${mobilePreview ? 'text-sm' : ''}`}>
                            {formData.title || 'Campaign Title'}
                          </p>
                          <p className={`text-[#FDF2F8]/60 ${mobilePreview ? 'text-xs' : 'text-sm'}`}>
                            {formData.subtitle || 'Subtitle here'}
                          </p>
                        </div>
                      </div>
                      {formData.discount_label && (
                        <span 
                          className={`px-2 py-1 text-xs font-bold rounded-full text-white ${mobilePreview ? 'self-start' : 'ml-auto'}`}
                          style={{ background: `linear-gradient(135deg, ${formData.gradient_from} 0%, ${formData.gradient_to} 100%)` }}
                        >
                          {formData.discount_label}
                        </span>
                      )}
                      {mobilePreview && formData.cta_text && (
                        <button
                          type="button"
                          className="w-full py-2 rounded-lg text-white text-sm font-medium"
                          style={{ background: `linear-gradient(135deg, ${formData.gradient_from} 0%, ${formData.gradient_to} 100%)` }}
                        >
                          {formData.cta_text}
                        </button>
                      )}
                    </div>
                  </div>
                ) : formData.banner_type === 'video' ? (
                  <div className="rounded-xl overflow-hidden mb-4 border border-[#4A0A66]">
                    {formData.video_url ? (
                      <video 
                        src={formData.video_url} 
                        className={`w-full object-cover ${mobilePreview ? 'h-auto aspect-video' : 'h-auto max-h-48'}`}
                        controls
                        muted
                      />
                    ) : (
                      <div className={`bg-[#1a0527] flex items-center justify-center ${mobilePreview ? 'h-44 aspect-video' : 'h-32'}`}>
                        <div className="text-center text-[#FDF2F8]/40">
                          <Video className="w-8 h-8 mx-auto mb-2" />
                          <p className="text-sm">Video preview</p>
                        </div>
                      </div>
                    )}
                    {mobilePreview && formData.cta_text && (
                      <div className="p-3 bg-[#1a0527] border-t border-[#4A0A66]">
                        <button
                          type="button"
                          className="w-full py-2 rounded-lg text-white text-sm font-medium bg-gradient-to-r from-[#df1283] to-[#FF7901]"
                        >
                          {formData.cta_text}
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="rounded-xl overflow-hidden mb-4 border border-[#4A0A66]">
                    {formData.image_url ? (
                      <img 
                        src={formData.image_url} 
                        alt="Banner preview" 
                        className={`w-full object-cover ${mobilePreview ? 'h-auto' : 'h-auto max-h-48'}`}
                      />
                    ) : (
                      <div className={`bg-[#1a0527] flex items-center justify-center text-[#FDF2F8]/40 ${mobilePreview ? 'h-32' : 'h-32'}`}>
                        Upload an image to preview
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Mobile Preview Note */}
              {mobilePreview && (
                <p className="text-xs text-[#0FFFE2]/70 text-center mb-4">
                  📱 This is how your banner will appear on mobile devices (320px width)
                </p>
              )}

              {/* Image Upload Section (only for image type) */}
              {formData.banner_type === 'image' && (
                <div className="space-y-3">
                  <label className="text-xs text-[#FDF2F8]/60 mb-1 block">
                    Banner Image (Recommended: 1200x200px or 1200x300px)
                  </label>
                  <div className="flex items-center gap-3">
                    <label className="flex-1 cursor-pointer">
                      <div className={`border-2 border-dashed border-[#4A0A66] rounded-lg p-4 text-center hover:border-[#df1283] transition-colors ${uploadingMedia ? 'opacity-50' : ''}`}>
                        {uploadingMedia ? (
                          <div className="flex items-center justify-center gap-2">
                            <div className="w-5 h-5 border-2 border-[#df1283] border-t-transparent rounded-full animate-spin" />
                            <span className="text-sm text-[#FDF2F8]/60">Uploading...</span>
                          </div>
                        ) : (
                          <>
                            <Upload className="w-6 h-6 text-[#FDF2F8]/40 mx-auto mb-2" />
                            <p className="text-sm text-[#FDF2F8]/60">Click to upload image</p>
                            <p className="text-xs text-[#FDF2F8]/40 mt-1">PNG, JPG up to 2MB</p>
                          </>
                        )}
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleMediaUpload(e, 'image')}
                        className="hidden"
                        disabled={uploadingMedia}
                      />
                    </label>
                    {formData.image_url && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))}
                        className="text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  
                  {/* Link URL for image banner */}
                  <div>
                    <label className="text-xs text-[#FDF2F8]/60 mb-1 block">Link URL (where the banner goes when clicked)</label>
                    <Input
                      value={formData.cta_link}
                      onChange={(e) => setFormData(prev => ({ ...prev, cta_link: e.target.value }))}
                      className="bg-[#1a0527] border-[#4A0A66] text-white"
                      placeholder="https://..."
                    />
                  </div>
                </div>
              )}

              {/* Video Upload Section (only for video type) */}
              {formData.banner_type === 'video' && (
                <div className="space-y-3">
                  <label className="text-xs text-[#FDF2F8]/60 mb-1 block">
                    Banner Video (Recommended: MP4, max 50MB)
                  </label>
                  <div className="flex items-center gap-3">
                    <label className="flex-1 cursor-pointer">
                      <div className={`border-2 border-dashed border-[#4A0A66] rounded-lg p-4 text-center hover:border-[#df1283] transition-colors ${uploadingMedia ? 'opacity-50' : ''}`}>
                        {uploadingMedia ? (
                          <div className="flex items-center justify-center gap-2">
                            <div className="w-5 h-5 border-2 border-[#df1283] border-t-transparent rounded-full animate-spin" />
                            <span className="text-sm text-[#FDF2F8]/60">Uploading...</span>
                          </div>
                        ) : (
                          <>
                            <Video className="w-6 h-6 text-[#FDF2F8]/40 mx-auto mb-2" />
                            <p className="text-sm text-[#FDF2F8]/60">Click to upload video</p>
                            <p className="text-xs text-[#FDF2F8]/40 mt-1">MP4, WebM up to 50MB</p>
                          </>
                        )}
                      </div>
                      <input
                        type="file"
                        accept="video/*"
                        onChange={(e) => handleMediaUpload(e, 'video')}
                        className="hidden"
                        disabled={uploadingMedia}
                      />
                    </label>
                    {formData.video_url && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setFormData(prev => ({ ...prev, video_url: '' }))}
                        className="text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  
                  {/* Or embed URL */}
                  <div>
                    <label className="text-xs text-[#FDF2F8]/60 mb-1 block">Or paste video embed URL</label>
                    <Input
                      value={formData.video_url}
                      onChange={(e) => setFormData(prev => ({ ...prev, video_url: e.target.value }))}
                      className="bg-[#1a0527] border-[#4A0A66] text-white"
                      placeholder="https://..."
                    />
                  </div>
                  
                  {/* CTA Button for video */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-[#FDF2F8]/60 mb-1 block">Button Text</label>
                      <Input
                        value={formData.cta_text}
                        onChange={(e) => setFormData(prev => ({ ...prev, cta_text: e.target.value }))}
                        className="bg-[#1a0527] border-[#4A0A66] text-white"
                        placeholder="Watch Now"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#FDF2F8]/60 mb-1 block">Link URL (when clicked)</label>
                      <Input
                        value={formData.cta_link}
                        onChange={(e) => setFormData(prev => ({ ...prev, cta_link: e.target.value }))}
                        className="bg-[#1a0527] border-[#4A0A66] text-white"
                        placeholder="https://..."
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Styled Banner Fields */}
              {formData.banner_type === 'styled' && (
                <>
                  {/* Title & Subtitle */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-[#FDF2F8]/60 mb-1 block">Title *</label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="bg-[#1a0527] border-[#4A0A66] text-white"
                    placeholder="Black Friday Sale!"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs text-[#FDF2F8]/60 mb-1 block">Subtitle</label>
                  <Input
                    value={formData.subtitle}
                    onChange={(e) => setFormData(prev => ({ ...prev, subtitle: e.target.value }))}
                    className="bg-[#1a0527] border-[#4A0A66] text-white"
                    placeholder="Limited time only"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="text-xs text-[#FDF2F8]/60 mb-1 block">Description</label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="bg-[#1a0527] border-[#4A0A66] text-white"
                  placeholder="Get 50% off Premium for a limited time..."
                />
              </div>

              {/* CTA */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-xs text-[#FDF2F8]/60 mb-1 block">Button Text</label>
                  <Input
                    value={formData.cta_text}
                    onChange={(e) => setFormData(prev => ({ ...prev, cta_text: e.target.value }))}
                    className="bg-[#1a0527] border-[#4A0A66] text-white"
                    placeholder="Get Started"
                  />
                </div>
                <div>
                  <label className="text-xs text-[#FDF2F8]/60 mb-1 block">Action Type</label>
                  <select
                    value={formData.cta_action}
                    onChange={(e) => setFormData(prev => ({ ...prev, cta_action: e.target.value }))}
                    className="w-full bg-[#1a0527] border border-[#4A0A66] text-white rounded-md px-3 py-2 text-sm"
                  >
                    {ACTIONS.map(action => (
                      <option key={action.value} value={action.value}>{action.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-[#FDF2F8]/60 mb-1 block">Link URL</label>
                  <Input
                    value={formData.cta_link}
                    onChange={(e) => setFormData(prev => ({ ...prev, cta_link: e.target.value }))}
                    className="bg-[#1a0527] border-[#4A0A66] text-white"
                    placeholder="https://..."
                    disabled={formData.cta_action !== 'link'}
                  />
                </div>
              </div>

              {/* Stripe Integration */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-[#FDF2F8]/60 mb-1 block flex items-center gap-1">
                    <CreditCard className="w-3 h-3" /> Stripe Price ID (optional)
                  </label>
                  <Input
                    value={formData.stripe_price_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, stripe_price_id: e.target.value }))}
                    className="bg-[#1a0527] border-[#4A0A66] text-white"
                    placeholder="price_xxx (for special promo pricing)"
                  />
                </div>
                <div>
                  <label className="text-xs text-[#FDF2F8]/60 mb-1 block">Discount Label</label>
                  <Input
                    value={formData.discount_label}
                    onChange={(e) => setFormData(prev => ({ ...prev, discount_label: e.target.value }))}
                    className="bg-[#1a0527] border-[#4A0A66] text-white"
                    placeholder="50% OFF"
                  />
                </div>
              </div>

              {/* Styling */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-xs text-[#FDF2F8]/60 mb-1 block">Icon</label>
                  <select
                    value={formData.icon}
                    onChange={(e) => setFormData(prev => ({ ...prev, icon: e.target.value }))}
                    className="w-full bg-[#1a0527] border border-[#4A0A66] text-white rounded-md px-3 py-2 text-sm"
                  >
                    {ICONS.map(icon => (
                      <option key={icon.value} value={icon.value}>{icon.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-[#FDF2F8]/60 mb-1 block">Gradient Start</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={formData.gradient_from}
                      onChange={(e) => setFormData(prev => ({ ...prev, gradient_from: e.target.value }))}
                      className="w-10 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={formData.gradient_from}
                      onChange={(e) => setFormData(prev => ({ ...prev, gradient_from: e.target.value }))}
                      className="bg-[#1a0527] border-[#4A0A66] text-white flex-1"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-[#FDF2F8]/60 mb-1 block">Gradient End</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={formData.gradient_to}
                      onChange={(e) => setFormData(prev => ({ ...prev, gradient_to: e.target.value }))}
                      className="w-10 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={formData.gradient_to}
                      onChange={(e) => setFormData(prev => ({ ...prev, gradient_to: e.target.value }))}
                      className="bg-[#1a0527] border-[#4A0A66] text-white flex-1"
                    />
                  </div>
                </div>
              </div>
                </>
              )}

              {/* Targeting - applies to both banner types */}
              <div>
                <label className="text-xs text-[#FDF2F8]/60 mb-2 block">Target Audience</label>
                <div className="flex flex-wrap gap-2">
                  {TIERS.map(tier => (
                    <button
                      key={tier.value}
                      type="button"
                      onClick={() => toggleTier(tier.value)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                        formData.target_tiers.includes(tier.value)
                          ? 'bg-[#df1283] text-white'
                          : 'bg-[#4A0A66]/50 text-[#FDF2F8]/60 hover:bg-[#4A0A66]'
                      }`}
                    >
                      {tier.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Scheduling */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-xs text-[#FDF2F8]/60 mb-1 block">Start Date</label>
                  <Input
                    type="datetime-local"
                    value={formData.start_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                    className="bg-[#1a0527] border-[#4A0A66] text-white"
                  />
                </div>
                <div>
                  <label className="text-xs text-[#FDF2F8]/60 mb-1 block">End Date</label>
                  <Input
                    type="datetime-local"
                    value={formData.end_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                    className="bg-[#1a0527] border-[#4A0A66] text-white"
                  />
                </div>
                <div>
                  <label className="text-xs text-[#FDF2F8]/60 mb-1 block">Priority</label>
                  <Input
                    type="number"
                    value={formData.priority}
                    onChange={(e) => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) || 0 }))}
                    className="bg-[#1a0527] border-[#4A0A66] text-white"
                    min="0"
                    max="100"
                  />
                </div>
              </div>

              {/* Sponsored Label */}
              <div className="p-4 bg-[#4A0A66]/20 rounded-xl border border-[#4A0A66]">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm text-[#FDF2F8]/80 font-medium">Sponsored Content</label>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, is_sponsored: !prev.is_sponsored }))}
                    className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                      formData.is_sponsored
                        ? 'bg-[#FF7901]/20 text-[#FF7901] border border-[#FF7901]/50'
                        : 'bg-[#4A0A66]/50 text-[#FDF2F8]/60 border border-[#4A0A66]'
                    }`}
                  >
                    {formData.is_sponsored ? 'Sponsored' : 'Not Sponsored'}
                  </button>
                </div>
                {formData.is_sponsored && (
                  <div>
                    <label className="text-xs text-[#FDF2F8]/60 mb-1 block">Sponsor Name (optional)</label>
                    <Input
                      value={formData.sponsor_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, sponsor_name: e.target.value }))}
                      placeholder="e.g., Sponsored by XYZ Brand"
                      className="bg-[#1a0527] border-[#4A0A66] text-white"
                    />
                    <p className="text-xs text-[#FDF2F8]/40 mt-1">
                      Users will see "Sponsored" label on this promo
                    </p>
                  </div>
                )}
              </div>

              {/* Dismiss Controls */}
              <div className="p-4 bg-[#4A0A66]/20 rounded-xl border border-[#4A0A66]">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <label className="text-sm text-[#FDF2F8]/80 font-medium">Allow Dismiss</label>
                    <p className="text-xs text-[#FDF2F8]/40">Can users close/hide this promo?</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, allow_dismiss: !prev.allow_dismiss }))}
                    className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                      formData.allow_dismiss
                        ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                        : 'bg-red-500/20 text-red-400 border border-red-500/50'
                    }`}
                  >
                    {formData.allow_dismiss ? 'Users Can Dismiss' : 'Cannot Dismiss'}
                  </button>
                </div>
                {formData.allow_dismiss && (
                  <div>
                    <label className="text-xs text-[#FDF2F8]/60 mb-2 block">Which tiers can dismiss?</label>
                    <div className="flex flex-wrap gap-2">
                      {TIERS.map(tier => (
                        <button
                          key={tier.value}
                          type="button"
                          onClick={() => {
                            const current = formData.dismiss_allowed_tiers || [];
                            const updated = current.includes(tier.value)
                              ? current.filter(t => t !== tier.value)
                              : [...current, tier.value];
                            setFormData(prev => ({ ...prev, dismiss_allowed_tiers: updated }));
                          }}
                          className={`px-3 py-1.5 rounded-full text-xs transition-colors ${
                            (formData.dismiss_allowed_tiers || []).includes(tier.value)
                              ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                              : 'bg-[#4A0A66]/50 text-[#FDF2F8]/40 border border-[#4A0A66]'
                          }`}
                        >
                          {tier.label}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-[#FDF2F8]/40 mt-2">
                      Unchecked tiers will NOT see the dismiss (X) button
                    </p>
                  </div>
                )}
              </div>

              {/* Active Toggle */}
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, is_active: !prev.is_active }))}
                  className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                    formData.is_active
                      ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                      : 'bg-[#4A0A66]/50 text-[#FDF2F8]/60 border border-[#4A0A66]'
                  }`}
                >
                  {formData.is_active ? <Power className="w-4 h-4" /> : <PowerOff className="w-4 h-4" />}
                  {formData.is_active ? 'Active' : 'Inactive'}
                </button>
                <p className="text-xs text-[#FDF2F8]/40">
                  {formData.is_active ? 'This campaign will show to users' : 'Save as draft - won\'t show to users'}
                </p>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-[#4A0A66]">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setShowEditor(false);
                    setEditingCampaign(null);
                  }}
                  className="text-[#FDF2F8]/60"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-gradient-to-r from-[#df1283] to-[#FF7901] text-white"
                >
                  {editingCampaign ? 'Update Campaign' : 'Create Campaign'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Campaigns List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin w-8 h-8 border-2 border-[#df1283] border-t-transparent rounded-full" />
        </div>
      ) : campaigns.length === 0 ? (
        <div className="text-center py-12 bg-[#4A0A66]/20 rounded-xl border border-[#4A0A66]/40">
          <Megaphone className="w-12 h-12 text-[#FDF2F8]/30 mx-auto mb-3" />
          <p className="text-[#FDF2F8]/60">No campaigns yet</p>
          <p className="text-sm text-[#FDF2F8]/40">Create your first promotional campaign</p>
        </div>
      ) : (
        <div className="space-y-3">
          {campaigns.map(campaign => {
            const CampaignIcon = ICONS.find(i => i.value === campaign.icon)?.icon || Sparkles;
            const isScheduled = campaign.start_date || campaign.end_date;
            
            return (
              <div 
                key={campaign.id}
                className="bg-[#4A0A66]/20 border border-[#4A0A66]/40 rounded-xl p-4"
              >
                <div className="flex items-center gap-4">
                  {/* Icon */}
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{
                      background: `linear-gradient(135deg, ${campaign.gradient_from || '#df1283'} 0%, ${campaign.gradient_to || '#FF7901'} 100%)`,
                    }}
                  >
                    <CampaignIcon className="w-6 h-6 text-white" />
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-white truncate">{campaign.title}</h4>
                      {campaign.discount_label && (
                        <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-[#FF7901]/20 text-[#FF7901]">
                          {campaign.discount_label}
                        </span>
                      )}
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        campaign.is_active 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-gray-500/20 text-gray-400'
                      }`}>
                        {campaign.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <p className="text-sm text-[#FDF2F8]/60 truncate">{campaign.subtitle || campaign.description || 'No description'}</p>
                    <div className="flex items-center gap-4 mt-1">
                      <span className="text-xs text-[#FDF2F8]/40 flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {campaign.target_tiers?.join(', ') || 'All users'}
                      </span>
                      {isScheduled && (
                        <span className="text-xs text-[#FDF2F8]/40 flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {campaign.start_date && format(new Date(campaign.start_date), 'MMM d')}
                          {campaign.start_date && campaign.end_date && ' - '}
                          {campaign.end_date && format(new Date(campaign.end_date), 'MMM d')}
                        </span>
                      )}
                      <span className="text-xs text-[#FDF2F8]/40">
                        Priority: {campaign.priority || 0}
                      </span>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggle(campaign.id)}
                      className={campaign.is_active ? 'text-green-400' : 'text-[#FDF2F8]/40'}
                    >
                      {campaign.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(campaign)}
                      className="text-[#FDF2F8]/60 hover:text-white"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(campaign.id)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Analytics Modal */}
      {showAnalytics && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border-2 border-[#0FFFE2]/50 rounded-xl p-6 max-w-4xl w-full my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <BarChart3 className="w-6 h-6 text-[#0FFFE2]" />
                Campaign Analytics
              </h3>
              <button 
                onClick={() => {
                  setShowAnalytics(false);
                  setSelectedCampaignAnalytics(null);
                }}
                className="text-[#FDF2F8]/50 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {loadingAnalytics ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0FFFE2]"></div>
              </div>
            ) : selectedCampaignAnalytics ? (
              // Detailed view for single campaign
              <div className="space-y-6">
                <button
                  onClick={() => setSelectedCampaignAnalytics(null)}
                  className="text-[#0FFFE2] text-sm hover:underline"
                >
                  ← Back to all campaigns
                </button>

                <div className="p-4 bg-[#4A0A66]/30 rounded-lg">
                  <h4 className="text-lg font-semibold text-white mb-2">
                    {selectedCampaignAnalytics.campaign?.title}
                  </h4>
                  <p className="text-sm text-[#FDF2F8]/60">
                    {selectedCampaignAnalytics.campaign?.banner_type} banner
                  </p>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-[#1a0527] border border-[#4A0A66] rounded-lg text-center">
                    <Eye className="w-5 h-5 mx-auto mb-2 text-[#FDF2F8]/50" />
                    <div className="text-2xl font-bold text-white">{selectedCampaignAnalytics.stats?.total_impressions || 0}</div>
                    <div className="text-xs text-[#FDF2F8]/50">Impressions</div>
                  </div>
                  <div className="p-4 bg-[#1a0527] border border-[#4A0A66] rounded-lg text-center">
                    <MousePointer className="w-5 h-5 mx-auto mb-2 text-[#df1283]" />
                    <div className="text-2xl font-bold text-[#df1283]">{selectedCampaignAnalytics.stats?.total_clicks || 0}</div>
                    <div className="text-xs text-[#FDF2F8]/50">Total Clicks</div>
                  </div>
                  <div className="p-4 bg-[#1a0527] border border-[#4A0A66] rounded-lg text-center">
                    <Users className="w-5 h-5 mx-auto mb-2 text-[#0FFFE2]" />
                    <div className="text-2xl font-bold text-[#0FFFE2]">{selectedCampaignAnalytics.stats?.unique_clicks || 0}</div>
                    <div className="text-xs text-[#FDF2F8]/50">Unique Clickers</div>
                  </div>
                  <div className="p-4 bg-[#1a0527] border border-[#4A0A66] rounded-lg text-center">
                    <TrendingUp className="w-5 h-5 mx-auto mb-2 text-[#22c55e]" />
                    <div className="text-2xl font-bold text-[#22c55e]">{selectedCampaignAnalytics.stats?.ctr || 0}%</div>
                    <div className="text-xs text-[#FDF2F8]/50">CTR</div>
                  </div>
                </div>

                {/* Video Stats (if video banner) */}
                {selectedCampaignAnalytics.campaign?.banner_type === 'video' && (
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 bg-[#1a0527] border border-[#4A0A66] rounded-lg text-center">
                      <PlayCircle className="w-5 h-5 mx-auto mb-2 text-[#FF7901]" />
                      <div className="text-2xl font-bold text-[#FF7901]">{selectedCampaignAnalytics.stats?.video_plays || 0}</div>
                      <div className="text-xs text-[#FDF2F8]/50">Video Plays</div>
                    </div>
                    <div className="p-4 bg-[#1a0527] border border-[#4A0A66] rounded-lg text-center">
                      <Play className="w-5 h-5 mx-auto mb-2 text-[#22c55e]" />
                      <div className="text-2xl font-bold text-[#22c55e]">{selectedCampaignAnalytics.stats?.video_completes || 0}</div>
                      <div className="text-xs text-[#FDF2F8]/50">Completions</div>
                    </div>
                    <div className="p-4 bg-[#1a0527] border border-[#4A0A66] rounded-lg text-center">
                      <TrendingUp className="w-5 h-5 mx-auto mb-2 text-purple-400" />
                      <div className="text-2xl font-bold text-purple-400">{selectedCampaignAnalytics.stats?.video_completion_rate || 0}%</div>
                      <div className="text-xs text-[#FDF2F8]/50">Completion Rate</div>
                    </div>
                  </div>
                )}

                {/* Who Clicked */}
                {selectedCampaignAnalytics.clickers?.length > 0 && (
                  <div>
                    <h5 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                      <Users className="w-4 h-4 text-[#0FFFE2]" />
                      Who Clicked ({selectedCampaignAnalytics.clickers.length} users)
                    </h5>
                    <div className="max-h-[200px] overflow-y-auto space-y-2">
                      {selectedCampaignAnalytics.clickers.map((clicker) => (
                        <div 
                          key={clicker.user_id || clicker.email}
                          className="flex items-center justify-between p-2 bg-[#1a0527] rounded-lg text-sm"
                        >
                          <div>
                            <span className="text-white">{clicker.name || 'Unknown'}</span>
                            <span className="text-[#FDF2F8]/50 ml-2">{clicker.email}</span>
                          </div>
                          <div className="text-[#FDF2F8]/50">
                            {clicker.click_count}x • {clicker.clicked_at ? format(new Date(clicker.clicked_at), 'MMM d, h:mm a') : ''}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : analyticsData ? (
              // Overview of all campaigns
              <div className="space-y-4">
                <p className="text-sm text-[#FDF2F8]/60 mb-4">
                  Click on a campaign to view detailed analytics including who clicked.
                </p>
                
                {analyticsData.length === 0 ? (
                  <div className="text-center py-8 text-[#FDF2F8]/50">
                    No campaigns yet. Create your first campaign to start tracking.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {analyticsData.map((campaign) => (
                      <div 
                        key={campaign.campaign_id}
                        onClick={() => fetchCampaignAnalytics(campaign.campaign_id)}
                        className="p-4 bg-[#1a0527] border border-[#4A0A66] rounded-lg cursor-pointer hover:border-[#0FFFE2] transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-white">{campaign.campaign_title}</span>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                campaign.is_active 
                                  ? 'bg-[#22c55e]/20 text-[#22c55e]' 
                                  : 'bg-[#FDF2F8]/10 text-[#FDF2F8]/50'
                              }`}>
                                {campaign.is_active ? 'Active' : 'Inactive'}
                              </span>
                              <span className="text-xs px-2 py-0.5 rounded bg-[#4A0A66]/50 text-[#FDF2F8]/50 capitalize">
                                {campaign.banner_type}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-6 text-sm">
                            <div className="text-center">
                              <div className="text-white font-semibold">{campaign.stats?.impressions || 0}</div>
                              <div className="text-xs text-[#FDF2F8]/40">Views</div>
                            </div>
                            <div className="text-center">
                              <div className="text-[#df1283] font-semibold">{campaign.stats?.clicks || 0}</div>
                              <div className="text-xs text-[#FDF2F8]/40">Clicks</div>
                            </div>
                            {campaign.banner_type === 'video' && (
                              <div className="text-center">
                                <div className="text-[#FF7901] font-semibold">{campaign.stats?.video_plays || 0}</div>
                                <div className="text-xs text-[#FDF2F8]/40">Plays</div>
                              </div>
                            )}
                            <div className="text-center">
                              <div className="text-[#0FFFE2] font-semibold">{campaign.stats?.ctr || 0}%</div>
                              <div className="text-xs text-[#FDF2F8]/40">CTR</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-[#FDF2F8]/50">
                Loading analytics...
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PromoCampaignManager;
